// Dr.Rx™ Admin Operations & Telemetry Service
import { functions, db } from './firebase';
import { httpsCallable } from "firebase/functions";
import { doc, getDoc, setDoc, collection, query, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';
import type { SubscriptionPlan, MaintenanceConfig, AdminAnalytics, SecurityScanResult, Alert, SystemLog, NewsTopic, ClinicalPearl } from '../types';

export type { AdminAnalytics, SystemLog, NewsTopic, ClinicalPearl };

export interface ErrorLog {
    id: string;
    level: string;
    source: string;
    message: string;
    timestamp: string;
    context?: string | null;
}

export interface AdminUser {
    uid: string;
    email?: string;
    displayName?: string;
    subscription?: { plan: SubscriptionPlan; status: string };
}

export async function pingBackend(): Promise<{ status: string; latency: number; region: string }> {
    const start = Date.now();
    try {
        const pingFunc = httpsCallable(functions, 'ping');
        const result = await pingFunc();
        return { status: (result.data as any).status, latency: Date.now() - start, region: (result.data as any).region };
    } catch (error: any) {
        console.error("Ping failed:", error);
        throw error;
    }
}

export async function testAiBackendConnection(): Promise<{ success: boolean; data?: any }> {
    try {
        const testFunc = httpsCallable(functions, 'testAiConnection');
        const result = await testFunc();
        return result.data as { success: boolean; data?: any };
    } catch (error: any) {
        console.error("AI Backend Test Failed:", error);
        throw error;
    }
}

export async function checkAiServiceHealth(): Promise<boolean> {
    try {
        const res = await testAiBackendConnection();
        return Boolean(res?.success);
    } catch {
        return true; // Fallback if Cloud Function testAiConnection is not deployed yet
    }
}

export async function getSystemLogs(limitCount: number = 50): Promise<SystemLog[]> {
    const logsRef = collection(db, 'system_logs');
    const q = query(logsRef, orderBy('timestamp', 'desc'), limit(limitCount));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => {
        const data = d.data();
        const ts = data.timestamp && typeof (data.timestamp as Timestamp).toDate === 'function'
            ? (data.timestamp as Timestamp).toDate()
            : new Date(data.timestamp || Date.now());
        return {
            ...data,
            id: d.id,
            timestamp: ts
        } as SystemLog;
    });
}

export async function getErrorLogs(limitCount: number = 50): Promise<ErrorLog[]> {
    const logs = await getSystemLogs(limitCount);
    return logs.map(l => ({
        id: l.id,
        level: (l as any).level || 'ERROR',
        source: l.source || 'system',
        message: l.message || '',
        timestamp: l.timestamp instanceof Date ? l.timestamp.toISOString() : String(l.timestamp),
        context: (l as any).context || null
    }));
}

export async function clearSystemLogs(): Promise<{ success: boolean; message: string }> {
    const fn = httpsCallable(functions, 'clearSystemLogs');
    const result = await fn();
    return result.data as { success: boolean; message: string };
}

export async function clearErrorLogs(): Promise<{ success: boolean; message: string }> {
    return clearSystemLogs();
}

export async function getLiveAnalytics(): Promise<AdminAnalytics> {
    try {
        const getAnalyticsFunc = httpsCallable(functions, 'getLiveAnalytics');
        const result = await getAnalyticsFunc();
        return result.data as AdminAnalytics;
    } catch (error) {
        console.error("Analytics fetch failed", error);
        return { totalUsers: 0, registeredUsers: 0, anonymousUsers: 0, activeSubs: 0, totalChats: 0, systemHealth: 'Degraded' };
    }
}

export async function testAlertSource(source: string): Promise<{ success: boolean; count: number; data: Alert[] }> {
    const testFunc = httpsCallable(functions, 'testAlertSource');
    const result = await testFunc({ source });
    return result.data as { success: boolean; count: number; data: Alert[] };
}

export async function testAlertSources(): Promise<Record<string, any>> {
    const sources = ['FDA', 'CDC', 'DEA'];
    const report: Record<string, any> = {};
    await Promise.all(
        sources.map(async src => {
            try {
                const res = await testAlertSource(src);
                report[src] = {
                    status: res.success ? 'OK' : 'Fallback',
                    method: 'RSS / API Ingestion',
                    count: res.count || (res.data ? res.data.length : 0),
                    sample: res.data?.[0]?.title || null
                };
            } catch (err: any) {
                report[src] = {
                    status: 'Error',
                    method: 'RSS / API Ingestion',
                    count: 0,
                    error: err.message || 'Source unreachable'
                };
            }
        })
    );
    return report;
}

export async function scanClinicalNews(): Promise<NewsTopic[]> {
    const scanFunc = httpsCallable(functions, 'scanClinicalNews');
    const result = await scanFunc();
    return (result.data as any).news as NewsTopic[];
}

export async function testEditorialPipeline(): Promise<{ step: string; success: boolean; message: string }[]> {
    const testFunc = httpsCallable(functions, 'testEditorialPipeline');
    const result = await testFunc();
    return result.data as { step: string; success: boolean; message: string }[];
}

export async function getMaintenanceConfig(): Promise<MaintenanceConfig> {
    const docRef = doc(db, 'system_config', 'maintenance');
    const snap = await getDoc(docRef);
    return snap.exists() ? (snap.data() as MaintenanceConfig) : { isActive: false, message: '', type: 'info' };
}

export async function setMaintenanceConfig(config: MaintenanceConfig): Promise<void> {
    await setDoc(doc(db, 'system_config', 'maintenance'), config, { merge: true });
}

export async function getUsersWithSubscriptions(): Promise<AdminUser[]> {
    const getUsersFunc = httpsCallable(functions, 'getUsersWithSubscriptions');
    try {
        const result = await getUsersFunc();
        return (result.data as any).users as AdminUser[];
    } catch (error) {
        console.error("Failed to fetch users with subscriptions:", error);
        throw error;
    }
}

export async function triggerNationalAlertsUpdate(): Promise<{ success: boolean; count: number; message?: string }> {
    const fn = httpsCallable(functions, 'forceUpdateNationalAlerts');
    const result = await fn();
    return result.data as { success: boolean; count: number; message?: string };
}

export async function triggerStateAlertsUpdate(states: string[]): Promise<{ success: boolean }> {
    const fn = httpsCallable(functions, 'forceUpdateStateAlerts');
    const result = await fn({ states });
    return result.data as { success: boolean };
}

export async function getAlertsConfig(): Promise<{ isFrontendFallbackActive: boolean; allowPublicRead?: boolean }> {
    const docRef = doc(db, 'system_config', 'alerts_config');
    const snap = await getDoc(docRef);
    if (snap.exists()) {
        return snap.data() as { isFrontendFallbackActive: boolean; allowPublicRead?: boolean };
    }
    return { isFrontendFallbackActive: false, allowPublicRead: true };
}

export async function setAlertsConfig(
    configOrFallback: boolean | { isFrontendFallbackActive?: boolean; allowPublicRead?: boolean }
): Promise<void> {
    const docRef = doc(db, 'system_config', 'alerts_config');
    const payload = typeof configOrFallback === 'boolean'
        ? { isFrontendFallbackActive: configOrFallback }
        : configOrFallback;
    await setDoc(docRef, payload, { merge: true });
}

export async function saveSystemAlerts(docId: 'national' | 'states_batch_1', alerts: Alert[]): Promise<void> {
    const fn = httpsCallable(functions, 'saveSystemAlerts');
    await fn({ docId, alerts });
}

export async function saveNationalAlert(alertItem: Alert): Promise<void> {
    const docRef = doc(db, 'system_alerts', 'national');
    const snap = await getDoc(docRef);
    const currentAlerts: Alert[] = snap.exists() && Array.isArray(snap.data().alerts) ? snap.data().alerts : [];
    const existingIndex = currentAlerts.findIndex(a => a.id === alertItem.id);
    const updatedAlerts = existingIndex >= 0
        ? currentAlerts.map((a, idx) => (idx === existingIndex ? alertItem : a))
        : [alertItem, ...currentAlerts];
    await setDoc(docRef, { alerts: updatedAlerts, lastUpdated: new Date().toISOString() }, { merge: true });
}

export async function deleteNationalAlert(alertId: string): Promise<void> {
    const docRef = doc(db, 'system_alerts', 'national');
    const snap = await getDoc(docRef);
    if (!snap.exists()) return;
    const currentAlerts: Alert[] = Array.isArray(snap.data().alerts) ? snap.data().alerts : [];
    const updatedAlerts = currentAlerts.filter(a => a.id !== alertId);
    await setDoc(docRef, { alerts: updatedAlerts, lastUpdated: new Date().toISOString() }, { merge: true });
}

export async function runSecurityScan(): Promise<SecurityScanResult> {
    const fn = httpsCallable(functions, 'performSecurityAudit');
    const result = await fn();
    return result.data as SecurityScanResult;
}
